/**
 * CR140: Schedule Editor Dialog
 * Create or edit a schedule with multi-merchant and per-source config
 * CR141: Add select all/none, forum picker, proper merchant loading
 * CR140.2: Timezone clarity - proper UTC ↔ ET conversion
 * CR156: Added "Run until" dropdown with helper text
 */

import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { CalendarIcon, X, CheckSquare, Square, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
// ScrollArea removed - using native overflow-y-auto for better modal scroll behavior
import type { 
  ScheduleWithMerchants, 
  ScheduleFormData, 
  ScheduleSource,
  SourceConfigs,
  WebSourceConfig,
  FlyerSourceConfig,
  ForumSourceConfig,
  ForumSource,
  ApprovalCheckpoint,
} from "@/lib/scheduler/types";
import {
  parseUtcToEtComponents,
  combineEtComponentsToUtc,
  getTimezoneLabel,
  getUtcDebugString,
} from "@/lib/scheduler/timezoneUtils";
import {
  CHECKPOINT_OPTIONS,
  DEFAULT_CHECKPOINT,
  getRunUntilConfig,
} from "@/lib/scheduler/runUntilConfig";

interface MerchantOption {
  merchant_id: string;
  merchant_name: string;
  category?: string | null;
}

interface ScheduleEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: ScheduleWithMerchants | null;
  merchants: MerchantOption[];
  forumSources: ForumSource[];
  promptPacks: Array<{ id: string; name: string }>;
  onSave: (data: ScheduleFormData) => Promise<boolean>;
}

const SOURCES: ScheduleSource[] = ["WEB", "FLYER", "FORUM"];

// Default configs - prompt_pack_id intentionally empty for WEB/FLYER (user must select)
// FORUM does NOT use prompt_pack_id
const DEFAULT_WEB_CONFIG: WebSourceConfig = {
  prompt_pack_id: "", // Required - user must select
  max_sources_to_scan: 3,
  timeout_ms: 65000,
};

const DEFAULT_FLYER_CONFIG: FlyerSourceConfig = {
  prompt_pack_id: "", // Required - user must select
  flyer_scope: "LATEST_ONLY",
  max_pages: 20,
};

// FORUM config does not include prompt_pack_id - it's not required for forums
const DEFAULT_FORUM_CONFIG: Omit<ForumSourceConfig, 'prompt_pack_id'> = {
  forum_target: "SAVED_QUERIES",
  lookback_days: 7,
  max_threads: 10,
  forum_ids: [],
};

export function ScheduleEditor({
  open,
  onOpenChange,
  schedule,
  merchants,
  forumSources,
  promptPacks,
  onSave,
}: ScheduleEditorProps) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [sourcesEnabled, setSourcesEnabled] = useState<ScheduleSource[]>([]);
  const [sourceConfigs, setSourceConfigs] = useState<SourceConfigs>({});
  const [maxConcurrentRuns, setMaxConcurrentRuns] = useState(3);
  const [runAt, setRunAt] = useState<Date | undefined>();
  const [runAtTime, setRunAtTime] = useState("09:00");
  const [timezone, setTimezone] = useState("America/Toronto"); // CR140.2: preserve schedule timezone
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [selectedForums, setSelectedForums] = useState<string[]>([]);
  const [forumSearch, setForumSearch] = useState("");
  const [showDisabledForums, setShowDisabledForums] = useState(false);
  // CR156: "Run until" checkpoint
  const [approvalCheckpoint, setApprovalCheckpoint] = useState<ApprovalCheckpoint>(DEFAULT_CHECKPOINT);
  // CR168: Flyer bucket source
  const [flyerBucketSourceId, setFlyerBucketSourceId] = useState<string | null>(null);

  // No auto-selection of prompt packs - user must explicitly choose

  // Reset form when dialog opens or schedule changes
  useEffect(() => {
    if (open) {
      if (schedule) {
        setName(schedule.name);
        setIsEnabled(schedule.is_enabled);
        setSourcesEnabled(schedule.sources_enabled);
        setSourceConfigs(schedule.source_configs);
        setMaxConcurrentRuns(schedule.max_concurrent_runs);
        setSelectedMerchants(schedule.merchants);
        // CR140.2: Preserve schedule timezone
        setTimezone(schedule.timezone ?? "America/Toronto");
        // CR141: Load forum_ids from config
        const forumConfig = schedule.source_configs.FORUM;
        setSelectedForums(forumConfig?.forum_ids || []);
        // CR156: Load approval checkpoint
        setApprovalCheckpoint(schedule.approval_checkpoint ?? DEFAULT_CHECKPOINT);
        // CR168: Load flyer bucket source
        setFlyerBucketSourceId(schedule.flyer_bucket_source_id ?? null);
        // CR140.2: Parse UTC to ET components to prevent time-shifting
        if (schedule.run_at) {
          const { date, time } = parseUtcToEtComponents(schedule.run_at);
          setRunAt(date);
          setRunAtTime(time);
        } else {
          setRunAt(undefined);
          setRunAtTime("09:00");
        }
      } else {
        // Reset for new schedule
        setName("");
        setIsEnabled(true);
        setSourcesEnabled([]);
        setSourceConfigs({});
        setMaxConcurrentRuns(3);
        setRunAt(undefined);
        setRunAtTime("09:00");
        setTimezone("America/Toronto"); // CR140.2: default for new schedules
        setSelectedMerchants([]);
        setSelectedForums([]);
        setApprovalCheckpoint(DEFAULT_CHECKPOINT); // CR156: default for new schedules
        setFlyerBucketSourceId(null); // CR168: default for new schedules
      }
      setMerchantSearch("");
      setForumSearch("");
    }
  }, [open, schedule]);

  const toggleSource = (source: ScheduleSource) => {
    setSourcesEnabled(prev => {
      const isCurrentlyEnabled = prev.includes(source);
      if (isCurrentlyEnabled) {
        // Remove source
        const newConfigs = { ...sourceConfigs };
        delete newConfigs[source];
        setSourceConfigs(newConfigs);
        return prev.filter(s => s !== source);
      } else {
        // Add source with default config
        // WEB/FLYER: prompt_pack_id starts empty (user must select)
        // FORUM: no prompt_pack_id required
        let baseConfig;
        if (source === "WEB") {
          baseConfig = { ...DEFAULT_WEB_CONFIG };
        } else if (source === "FLYER") {
          baseConfig = { ...DEFAULT_FLYER_CONFIG };
        } else {
          // FORUM - no prompt_pack_id, but include current forum selections
          baseConfig = { ...DEFAULT_FORUM_CONFIG, forum_ids: selectedForums };
        }
        setSourceConfigs(prev => ({ ...prev, [source]: baseConfig }));
        return [...prev, source];
      }
    });
  };

  const updateSourceConfig = <T extends keyof SourceConfigs>(
    source: T,
    updates: Partial<SourceConfigs[T]>
  ) => {
    setSourceConfigs(prev => ({
      ...prev,
      [source]: { ...prev[source], ...updates },
    }));
  };

  // CR141: Filtered merchants with search
  const filteredMerchants = useMemo(() => {
    const search = merchantSearch.toLowerCase();
    return merchants.filter(m =>
      m.merchant_name.toLowerCase().includes(search) ||
      m.merchant_id.toLowerCase().includes(search)
    );
  }, [merchants, merchantSearch]);

  // CR141: Filtered forums with search and enabled filter
  const filteredForums = useMemo(() => {
    const search = forumSearch.toLowerCase();
    return forumSources.filter(f => {
      const matchesSearch = f.name.toLowerCase().includes(search) ||
        f.endpoint_url.toLowerCase().includes(search);
      const matchesEnabled = showDisabledForums || f.is_enabled;
      return matchesSearch && matchesEnabled;
    });
  }, [forumSources, forumSearch, showDisabledForums]);

  const toggleMerchant = (merchantId: string) => {
    setSelectedMerchants(prev =>
      prev.includes(merchantId)
        ? prev.filter(m => m !== merchantId)
        : [...prev, merchantId]
    );
  };

  const toggleForum = (forumId: string) => {
    setSelectedForums(prev => {
      const newSelection = prev.includes(forumId)
        ? prev.filter(f => f !== forumId)
        : [...prev, forumId];
      // Update config in real-time
      if (sourcesEnabled.includes("FORUM")) {
        updateSourceConfig("FORUM", { forum_ids: newSelection });
      }
      return newSelection;
    });
  };

  // CR141: Select all filtered merchants
  const selectAllMerchants = () => {
    const filteredIds = filteredMerchants.map(m => m.merchant_id);
    setSelectedMerchants(prev => {
      const newSet = new Set([...prev, ...filteredIds]);
      return Array.from(newSet);
    });
  };

  // CR141: Clear all merchant selections
  const clearAllMerchants = () => {
    setSelectedMerchants([]);
  };

  // CR141: Select all filtered forums
  const selectAllForums = () => {
    const filteredIds = filteredForums.map(f => f.id);
    setSelectedForums(prev => {
      const newSelection = Array.from(new Set([...prev, ...filteredIds]));
      if (sourcesEnabled.includes("FORUM")) {
        updateSourceConfig("FORUM", { forum_ids: newSelection });
      }
      return newSelection;
    });
  };

  // CR141: Clear all forum selections
  const clearAllForums = () => {
    setSelectedForums([]);
    if (sourcesEnabled.includes("FORUM")) {
      updateSourceConfig("FORUM", { forum_ids: [] });
    }
  };

  // Check if WEB or FLYER is enabled (needs merchants)
  const needsMerchants = sourcesEnabled.includes("WEB") || sourcesEnabled.includes("FLYER");
  // Check if FORUM is enabled (needs forums)
  const needsForums = sourcesEnabled.includes("FORUM");

  // Calculate expected runs
  const merchantRunCount = needsMerchants ? selectedMerchants.length * sourcesEnabled.filter(s => s !== "FORUM").length : 0;
  const forumRunCount = needsForums ? selectedForums.length : 0;
  const expectedRuns = merchantRunCount + forumRunCount;
  const isOverLimit = expectedRuns > 50;

  // Build blocking reasons for debugging
  const blockingReasons: string[] = [];
  if (!name.trim()) {
    blockingReasons.push("Name required");
  }
  if (sourcesEnabled.length === 0) {
    blockingReasons.push("Select at least 1 source (WEB/FLYER/FORUM)");
  }
  if (needsMerchants && selectedMerchants.length === 0) {
    blockingReasons.push("Select at least 1 merchant for WEB/FLYER");
  }
  if (needsForums && selectedForums.length === 0) {
    blockingReasons.push("Select at least 1 forum for FORUM");
  }
  if (isOverLimit) {
    blockingReasons.push("Expected runs exceeds 50 limit");
  }
  // Check prompt packs for WEB and FLYER only (FORUM does not require prompt pack)
  if (sourcesEnabled.includes("WEB")) {
    const webConfig = sourceConfigs.WEB;
    if (!webConfig?.prompt_pack_id) {
      blockingReasons.push("WEB: Select a Prompt Pack");
    }
  }
  if (sourcesEnabled.includes("FLYER")) {
    const flyerConfig = sourceConfigs.FLYER;
    if (!flyerConfig?.prompt_pack_id) {
      blockingReasons.push("FLYER: Select a Prompt Pack");
    }
  }

  const canSave = blockingReasons.length === 0;

  const handleSave = async () => {
    if (!canSave) return;

    // CR140.2: Combine date and time as ET, convert to UTC for storage
    let fullRunAtUtc: Date | null = null;
    if (runAt && runAtTime) {
      const utcIso = combineEtComponentsToUtc(runAt, runAtTime);
      fullRunAtUtc = new Date(utcIso);
    }

    // Ensure forum_ids is in the config
    const finalConfigs = { ...sourceConfigs };
    if (sourcesEnabled.includes("FORUM") && finalConfigs.FORUM) {
      finalConfigs.FORUM = { ...finalConfigs.FORUM, forum_ids: selectedForums };
    }

    setSaving(true);
    try {
      // CR140.2: Dev debug log for timezone conversion
      if (import.meta.env.DEV && runAt && runAtTime) {
        console.log("[ScheduleEditor] Save timezone debug:", {
          etDate: runAt.toDateString(),
          etTime: runAtTime,
          computedUtcIso: fullRunAtUtc?.toISOString(),
          timezone,
        });
      }

      const success = await onSave({
        name: name.trim(),
        is_enabled: isEnabled,
        timezone, // CR140.2: preserve stored timezone instead of hardcoding
        sources_enabled: sourcesEnabled,
        source_configs: finalConfigs,
        max_concurrent_runs: maxConcurrentRuns,
        run_at: fullRunAtUtc,
        merchant_ids: selectedMerchants,
        forum_ids: selectedForums,
        approval_checkpoint: approvalCheckpoint, // CR156: "Run until" checkpoint
        flyer_bucket_source_id: flyerBucketSourceId, // CR168: Flyer bucket source
      });

      if (success) {
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle>
            {schedule ? "Edit Schedule" : "Create Schedule"}
          </DialogTitle>
          <DialogDescription>
            Configure a scheduled batch run with merchants and/or forum sources.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Schedule Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Daily Morning Scan"
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="enabled"
                    checked={isEnabled}
                    onCheckedChange={setIsEnabled}
                  />
                  <Label htmlFor="enabled">Enabled</Label>
                </div>
              </div>
            </div>

            {/* Run At - CR140.2: Enhanced with timezone display */}
            <TooltipProvider>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Run At (One-Time)</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="text-xs max-w-xs">
                      <p>Times are in Eastern Time ({getTimezoneLabel()})</p>
                      <p className="text-muted-foreground mt-1">Stored as UTC in database</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal flex-1",
                          !runAt && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {runAt ? format(runAt, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={runAt}
                        onSelect={setRunAt}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={runAtTime}
                    onChange={e => setRunAtTime(e.target.value)}
                    className="w-32"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Timezone: {getTimezoneLabel()}
                  </p>
                  {runAt && runAtTime && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-xs text-muted-foreground cursor-help underline decoration-dotted">
                          View UTC
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        <p>{getUtcDebugString(combineEtComponentsToUtc(runAt, runAtTime))}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </TooltipProvider>

            {/* Sources */}
            <div className="space-y-2">
              <Label>Sources</Label>
              <div className="flex gap-2">
                {SOURCES.map(source => (
                  <Button
                    key={source}
                    variant={sourcesEnabled.includes(source) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleSource(source)}
                  >
                    {source}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                WEB and FLYER use merchant selection and run through the pipeline.
              </p>
              {/* CR161: Forum MVP explanation */}
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">FORUM (MVP):</span> Kicks off Community Signal Diagnostics so you can review detected opportunities. This does not automatically scan merchants or publish deals yet.
              </p>
            </div>

            {/* CR156: Run Until dropdown */}
            <div className="space-y-2">
              <Label>Run until</Label>
              <Select
                value={approvalCheckpoint}
                onValueChange={(v) => setApprovalCheckpoint(v as ApprovalCheckpoint)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select automation level" />
                </SelectTrigger>
                <SelectContent>
                  {CHECKPOINT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Steps up to this checkpoint run automatically. Later steps stay manual so you can review before publishing.
              </p>
              {/* CR161: Note when FORUM is selected */}
              {sourcesEnabled.includes('FORUM') && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 border border-muted">
                  <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> FORUM currently ignores "Run until" and only triggers Community Signal Diagnostics.
                  </p>
                </div>
              )}
              {/* Warning for AUTO_PUBLISH */}
              {approvalCheckpoint === 'AUTO_PUBLISH' && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-accent/10 border border-accent/30">
                  <AlertTriangle className="h-4 w-4 text-status-running flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-accent-foreground">
                    {getRunUntilConfig(approvalCheckpoint).warningText}
                  </p>
                </div>
              )}
            </div>

            {/* WEB Configuration - ABOVE merchants */}
            {sourcesEnabled.includes("WEB") && (
              <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">WEB Configuration</Label>
                </div>
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label>Prompt Pack *</Label>
                    <Select
                      value={sourceConfigs.WEB?.prompt_pack_id || ""}
                      onValueChange={v => updateSourceConfig("WEB", { prompt_pack_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select prompt pack" />
                      </SelectTrigger>
                      <SelectContent>
                        {promptPacks.map(pack => (
                          <SelectItem key={pack.id} value={pack.id}>
                            {pack.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label className="text-sm">Max Sources to Scan</Label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={sourceConfigs.WEB?.max_sources_to_scan || 3}
                        onChange={e => updateSourceConfig("WEB", { max_sources_to_scan: parseInt(e.target.value) || 3 })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-sm">Timeout (ms)</Label>
                      <Input
                        type="number"
                        min={10000}
                        max={120000}
                        value={sourceConfigs.WEB?.timeout_ms || 65000}
                        onChange={e => updateSourceConfig("WEB", { timeout_ms: parseInt(e.target.value) || 65000 })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* FLYER Configuration - ABOVE merchants */}
            {sourcesEnabled.includes("FLYER") && (
              <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">FLYER Configuration</Label>
                </div>
                <div className="grid gap-4">
                  {/* CR168: Flyer Bucket Source selector */}
                  <div className="grid gap-2">
                    <Label>Flyer Bucket Source (optional)</Label>
                    <Select
                      value={flyerBucketSourceId || "none"}
                      onValueChange={v => setFlyerBucketSourceId(v === "none" ? null : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select bucket source..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No bucket (manual upload)</SelectItem>
                        {/* Bucket sources will be loaded from props when available */}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Configure in Operations → Flyer Bucket Sources
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Label>Prompt Pack *</Label>
                    <Select
                      value={sourceConfigs.FLYER?.prompt_pack_id || ""}
                      onValueChange={v => updateSourceConfig("FLYER", { prompt_pack_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select prompt pack" />
                      </SelectTrigger>
                      <SelectContent>
                        {promptPacks.map(pack => (
                          <SelectItem key={pack.id} value={pack.id}>
                            {pack.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label className="text-sm">Flyer Scope</Label>
                      <Select
                        value={sourceConfigs.FLYER?.flyer_scope || "LATEST_ONLY"}
                        onValueChange={v => updateSourceConfig("FLYER", { flyer_scope: v as "LATEST_ONLY" | "LAST_N_DAYS" })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LATEST_ONLY">Latest Only</SelectItem>
                          <SelectItem value="LAST_N_DAYS">Last N Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-sm">Max Pages</Label>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={sourceConfigs.FLYER?.max_pages || 20}
                        onChange={e => updateSourceConfig("FLYER", { max_pages: parseInt(e.target.value) || 20 })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Merchants - show when WEB or FLYER is enabled */}
            {needsMerchants && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Merchants ({selectedMerchants.length} selected)</Label>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={selectAllMerchants}
                      className="h-7 text-xs"
                    >
                      <CheckSquare className="mr-1 h-3 w-3" />
                      Select All ({filteredMerchants.length})
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllMerchants}
                      disabled={selectedMerchants.length === 0}
                      className="h-7 text-xs"
                    >
                      <Square className="mr-1 h-3 w-3" />
                      Clear
                    </Button>
                  </div>
                </div>
                
                {selectedMerchants.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selectedMerchants.slice(0, 8).map(id => (
                      <Badge key={id} variant="secondary" className="text-xs">
                        {merchants.find(m => m.merchant_id === id)?.merchant_name || id}
                        <button
                          className="ml-1 hover:text-destructive"
                          onClick={() => toggleMerchant(id)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedMerchants.length > 8 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedMerchants.length - 8} more
                      </Badge>
                    )}
                  </div>
                )}

                <Input
                  placeholder="Search merchants..."
                  value={merchantSearch}
                  onChange={e => setMerchantSearch(e.target.value)}
                />
                <div className="border rounded-md h-[260px] overflow-y-auto">
                  {filteredMerchants.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                      No merchants found
                    </div>
                  ) : (
                    filteredMerchants.map(merchant => (
                      <div
                        key={merchant.merchant_id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleMerchant(merchant.merchant_id)}
                      >
                        <Checkbox
                          checked={selectedMerchants.includes(merchant.merchant_id)}
                          className="pointer-events-none"
                        />
                        <span className="text-sm flex-1">{merchant.merchant_name}</span>
                        {merchant.category && (
                          <Badge variant="outline" className="text-xs">
                            {merchant.category}
                          </Badge>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {merchants.length} total merchants available
                </p>
              </div>
            )}

            {/* Forums - show when FORUM is enabled */}
            {needsForums && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Forums ({selectedForums.length} selected)</Label>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={selectAllForums}
                      className="h-7 text-xs"
                    >
                      <CheckSquare className="mr-1 h-3 w-3" />
                      Select All ({filteredForums.length})
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllForums}
                      disabled={selectedForums.length === 0}
                      className="h-7 text-xs"
                    >
                      <Square className="mr-1 h-3 w-3" />
                      Clear
                    </Button>
                  </div>
                </div>
                
                {selectedForums.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selectedForums.slice(0, 6).map(id => (
                      <Badge key={id} variant="secondary" className="text-xs">
                        {forumSources.find(f => f.id === id)?.name || id}
                        <button
                          className="ml-1 hover:text-destructive"
                          onClick={() => toggleForum(id)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {selectedForums.length > 6 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedForums.length - 6} more
                      </Badge>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    placeholder="Search forums..."
                    value={forumSearch}
                    onChange={e => setForumSearch(e.target.value)}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      id="showDisabled"
                      checked={showDisabledForums}
                      onCheckedChange={setShowDisabledForums}
                    />
                    <Label htmlFor="showDisabled" className="text-xs whitespace-nowrap">
                      Show disabled
                    </Label>
                  </div>
                </div>
                <div className="border rounded-md h-[200px] overflow-y-auto">
                  {filteredForums.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                      No forums found
                    </div>
                  ) : (
                    filteredForums.map(forum => (
                      <div
                        key={forum.id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleForum(forum.id)}
                      >
                        <Checkbox
                          checked={selectedForums.includes(forum.id)}
                          className="pointer-events-none"
                        />
                        <span className="text-sm flex-1">{forum.name}</span>
                        <Badge variant={forum.is_enabled ? "default" : "outline"} className="text-xs">
                          {forum.source_type}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {forumSources.filter(f => f.is_enabled).length} enabled / {forumSources.length} total forums
                </p>
              </div>
            )}

            {/* FORUM Configuration - only show config options, not prompt pack */}
            {sourcesEnabled.includes("FORUM") && (
              <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">FORUM Configuration</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Forums are processed using selected forum sources. No prompt pack required.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label className="text-sm">Lookback Days</Label>
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={sourceConfigs.FORUM?.lookback_days || 7}
                      onChange={e => updateSourceConfig("FORUM", { lookback_days: parseInt(e.target.value) || 7 })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-sm">Max Threads</Label>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={sourceConfigs.FORUM?.max_threads || 10}
                      onChange={e => updateSourceConfig("FORUM", { max_threads: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Execution Settings */}
            <div className="space-y-4">
              <Label>Execution Settings</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-sm text-muted-foreground">Max Concurrent Runs</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={maxConcurrentRuns}
                    onChange={e => setMaxConcurrentRuns(parseInt(e.target.value) || 3)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-sm text-muted-foreground">Expected Runs</Label>
                  <div className={cn(
                    "flex items-center h-10 px-3 rounded-md border text-sm",
                    isOverLimit ? "border-destructive bg-destructive/10" : "bg-muted"
                  )}>
                    <span className={cn("font-medium", isOverLimit && "text-destructive")}>
                      {expectedRuns}
                      {needsMerchants && needsForums && (
                        <span className="text-muted-foreground ml-1">
                          ({merchantRunCount} merchant + {forumRunCount} forum)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {isOverLimit && (
                <p className="text-sm text-destructive">
                  Expected runs exceeds the limit of 50. Reduce selections.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4 flex-shrink-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end bg-background">
          {!canSave && blockingReasons.length > 0 && (
            <p className="text-xs text-destructive text-left sm:mr-auto">
              {blockingReasons[0]}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              {saving ? "Saving..." : schedule ? "Save Changes" : "Create Schedule"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
